import { NewOpportunity } from '../../lib/types'
import { convertOpportunityMoneyFieldsToCents } from './opportunity-form-money'

export const CMS_DEPRECATED_OPPORTUNITY_FIELDS = [
  'video',
  'images',
  'highlights',
  'key_factors',
  'terms',
] as const

/** Omitted on PATCH so legacy DB values are not cleared; managed in Sanity or asset class data. */
export const CMS_STRIPPED_ON_UPDATE = [
  ...CMS_DEPRECATED_OPPORTUNITY_FIELDS,
  'minimum_return',
  'maximum_return',
  'coupon_payment_interval_in_months',
] as const

export const CMS_DEPRECATED_CREATE_DEFAULTS: Pick<
  NewOpportunity,
  'video' | 'images' | 'highlights' | 'key_factors' | 'terms'
> = {
  video: '',
  images: [],
  highlights: [],
  key_factors: [],
  terms: {
    level1: { header: '', termObjects: [{ title: '', content: '' }] },
    level2: { header: '', termObjects: [{ title: '', content: '' }] },
  },
}

export const stripMetadataCompanyWebsite = (metadata: NewOpportunity['metadata']) => {
  if (!metadata?.company) {
    return metadata
  }
  const { website: _website, ...company } = metadata.company
  return { ...metadata, company }
}

const normalizeReferenceValuation = (
  metadata: NewOpportunity['metadata'],
): NewOpportunity['metadata'] => {
  if (!metadata) return metadata

  const referenceValuation = metadata.reference_valuation
  if (!referenceValuation) {
    const next = { ...metadata }
    delete next.reference_valuation
    return next
  }

  const currency =
    typeof referenceValuation.currency === 'string' ? referenceValuation.currency.trim() : ''
  const amount = Number(referenceValuation.amount)

  if (!currency || !Number.isFinite(amount) || amount <= 0) {
    const next = { ...metadata }
    delete next.reference_valuation
    return next
  }

  return {
    ...metadata,
    reference_valuation: { currency, amount },
  }
}

const normalizeOpportunityDomain = (value: NewOpportunity['opportunity_domain']) => {
  if (typeof value !== 'string') {
    return value ?? null
  }
  const domain = value.trim().toLowerCase()
  return domain.length > 0 ? domain : null
}

export const prepareOpportunityPayloadForApi = (
  opportunity: NewOpportunity,
  mode: 'create' | 'update',
): NewOpportunity => {
  const withCents = convertOpportunityMoneyFieldsToCents(opportunity)
  const metadata = normalizeReferenceValuation(stripMetadataCompanyWebsite(withCents.metadata))
  const payload = {
    ...withCents,
    metadata,
    opportunity_domain: normalizeOpportunityDomain(withCents.opportunity_domain),
  }

  if (mode === 'create') {
    return { ...payload, ...CMS_DEPRECATED_CREATE_DEFAULTS }
  }

  CMS_STRIPPED_ON_UPDATE.forEach((field) => {
    delete (payload as Record<string, unknown>)[field]
  })

  return payload
}
